'use client';

import { c as _c } from "react/compiler-runtime";
import { type UpdateSavedViewAudienceInput, useApi } from '@repo/api';
import { useAccess } from '@repo/auth';
import { type FormUserMultiSelectUser, UserMultiSelect } from '@repo/form';
import { permissions } from '@repo/permissions';
import type { SavedViewAudienceEntry, SavedViewSummary } from '@repo/schema/table-configuration';
import { HIDDEN_ROLES } from '@repo/sdk/constants';
import { formatRoleName } from '@repo/sdk/helpers';
import { useTableConfiguration } from '@repo/table/table-configuration';
import { useInfiniteQuery, useQuery } from '@repo/tanstack/query';
import { Button, Label, Tags, TagsContent, TagsEmpty, TagsGroup, TagsInput, TagsItem, TagsList, TagsTrigger, TagsValue, toast } from '@repo/ui/components';
import { CheckIcon } from '@repo/ui/icons';
import { type Ref, useRef, useState } from 'react';
import { ConfirmWarning } from './confirm-warning.tsx';
import { productError } from './product-error.ts';
type Mode = SavedViewSummary['audience_summary']['mode'];
const MODES: readonly {
  label: string;
  mode: Mode;
}[] = [{
  label: 'Only me',
  mode: 'owner'
}, {
  label: 'Selected',
  mode: 'selected'
}, {
  label: 'Everyone',
  mode: 'all'
}];

/** Who a view is shared with. People are the picker's Users; Roles are ids and names. */
export type Audience = {
  mode: Mode;
  roles: AudienceRole[];
  users: FormUserMultiSelectUser[];
};
type AudienceRole = {
  id: number;
  name: string;
};

/** Selected with nobody chosen stays private. */
export const toAudienceInput = ({
  mode,
  roles,
  users
}: Audience) => {
  if (mode !== 'selected') return {
    mode
  };
  if (!users.length && !roles.length) return {
    mode: 'owner' as const
  };
  return {
    mode,
    role_ids: roles.map(role => role.id),
    user_ids: users.map(user => user.id)
  };
};

/** Ids in `items` but not in `others`; `undefined` when none, so the request leaves it out. */
const missingFrom = (items: readonly {
  id: number;
}[], others: readonly {
  id: number;
}[]) => {
  const missing = items.filter(item => !others.some(other => other.id === item.id)).map(item => item.id);
  return missing.length ? missing : undefined;
};

/** `undefined` for no ids, so the request leaves the field out. */
const someIds = (ids: number[] = []) => ids.length ? ids : undefined;

/**
 * Owner-only sharing of a saved view. Every change saves at once, as the difference between the
 * chosen people and Roles and the ones the view is shared with now, and can be undone from the
 * toast that follows. Everyone waits for an inline warning first.
 */
export const ViewSharing = ({
  onChange,
  view
}: {
  onChange: (_view: SavedViewSummary) => void;
  view: SavedViewSummary;
}) => {
  const api = useApi();
  const controller = useTableConfiguration();
  const [choosing, setChoosing] = useState(false);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const everyoneRef = useRef<HTMLButtonElement>(null);
  const latestSave = useRef(0);
  const [error, setError] = useState<null | string>(null);
  const [isPending, setIsPending] = useState(false);
  const summary = view.audience_summary;
  const mode = choosing ? 'selected' : summary.mode;
  const audiencePath = {
    id: view.id,
    tableKey: controller.definition.tableKey
  };
  // Organisations share with far fewer than 100 people; the field says so when there are more.
  const sharedUsers = useInfiniteQuery({
    ...api.tableConfigurations.audience.queryOptions({
      input: {
        perPage: 100,
        type: 'user'
      },
      path: audiencePath
    }),
    enabled: summary.mode === 'selected'
  });
  const sharedRoles = useInfiniteQuery({
    ...api.tableConfigurations.audience.queryOptions({
      input: {
        perPage: 100,
        type: 'role'
      },
      path: audiencePath
    }),
    enabled: summary.mode === 'selected'
  });
  const current: Audience = {
    mode,
    roles: (sharedRoles.data?.pages[0]?.data ?? []).filter(entry => entry.type === 'role'),
    users: (sharedUsers.data?.pages[0]?.data ?? []).flatMap(toPickerUser)
  };
  const {
    allowed: canAddUsers
  } = useAccess({
    permissions: [permissions.users.list]
  });
  const {
    allowed: canAddRoles
  } = useAccess({
    permissions: [permissions.roles.list]
  });
  // The API only adds active Users, so inactive people can't be shared with again.
  const inactiveUserIds = (sharedUsers.data?.pages[0]?.data ?? []).flatMap(entry_0 => entry_0.type === 'user' && !entry_0.active ? [entry_0.id] : []);
  // A save refetches the audience without waiting for it; until it lands the lists are stale, so
  // nothing can change and no Undo is built from them.
  const busy = isPending || sharedUsers.isFetching || sharedRoles.isFetching;
  // Every person and Role is listed, so leaving Selected can be put back exactly.
  const audienceListed = summary.mode === 'selected' && sharedUsers.isSuccess && sharedRoles.isSuccess && !sharedUsers.hasNextPage && !sharedRoles.hasNextPage;

  /** The request that puts back what `input` changes, or `null` when it can't be put back exactly. */
  const toUndo = (input: UpdateSavedViewAudienceInput): null | UpdateSavedViewAudienceInput => {
    let undo: UpdateSavedViewAudienceInput;
    if (summary.mode !== 'selected') undo = {
      mode: summary.mode
    };else if (input.mode === 'selected') {
      undo = {
        add_role_ids: input.remove_role_ids,
        add_user_ids: input.remove_user_ids,
        mode: 'selected',
        remove_role_ids: input.add_role_ids,
        remove_user_ids: input.add_user_ids
      };
    } else {
      // Leaving Selected drops every person and Role, so going back adds them all again.
      if (!audienceListed) return null;
      undo = {
        add_role_ids: someIds(current.roles.map(role => role.id)),
        add_user_ids: someIds(current.users.map(user => user.id)),
        mode: 'selected'
      };
    }
    const addsUsers = undo.mode === 'all' || Boolean(undo.add_user_ids);
    if (undo.add_user_ids?.some(id => inactiveUserIds.includes(id))) return null;
    if (addsUsers && !canAddUsers || undo.add_role_ids && !canAddRoles) return null;
    return undo;
  };

  /** Saves `input`; a save that isn't itself an undo offers one. */
  const save = async (input_0: UpdateSavedViewAudienceInput, restoring = false) => {
    const undo_0 = restoring ? null : toUndo(input_0);
    // Only the latest saved change can be undone; an older Undo would land on top of a newer one.
    const generation = ++latestSave.current;
    setIsPending(true);
    setError(null);
    try {
      onChange(await controller.updateAudience(view.id, input_0));
      setChoosing(false);
      if (restoring) toast.success('Sharing restored');else toast.success('Sharing saved', {
        action: undo_0 ? {
          label: 'Undo',
          onClick: () => {
            if (generation === latestSave.current) void save(undo_0, true);
          }
        } : undefined,
        id: `saved-view-sharing-${view.id}`
      });
    } catch (failure) {
      // A failed change replaced nothing, so the last saved change's Undo works again.
      if (latestSave.current === generation) latestSave.current = generation - 1;
      const message = productError(failure instanceof Error ? failure : new Error(String(failure)));
      // Undo is pressed in the toast, which can outlive this page.
      if (restoring) toast.error(message);else setError(message);
    }
    setIsPending(false);
  };
  const change = (next: Audience) => {
    setConfirmingAll(false);
    if (next.mode !== 'selected') {
      // Sharing with Everyone waits for the warning's confirmation.
      if (next.mode === 'all' && summary.mode !== 'all') return setConfirmingAll(true);
      setChoosing(false);
      if (next.mode !== summary.mode) void save({
        mode: next.mode
      });
      return;
    }
    // Choosing Selected saves nothing until someone is added.
    if (next.mode !== mode) return setChoosing(true);
    const changes = {
      add_role_ids: missingFrom(next.roles, current.roles),
      add_user_ids: missingFrom(next.users, current.users),
      remove_role_ids: missingFrom(current.roles, next.roles),
      remove_user_ids: missingFrom(current.users, next.users)
    };
    // Pressing Selected again, or closing a picker unchanged, has nothing to save.
    if (Object.values(changes).some(Boolean)) void save({
      ...changes,
      mode: 'selected'
    });
  };
  const unlisted = summary.user_count - current.users.length;
  return <div className="flex flex-col gap-4" data-sentry-component="ViewSharing" data-sentry-source-file="view-sharing.tsx">
      <p className="text-xs text-muted-foreground">
        People you share with can use and pin this view. Only you can change it.
      </p>
      <AudienceFields disabled={busy} everyoneRef={everyoneRef} onChange={change} value={current} data-sentry-element="AudienceFields" data-sentry-source-file="view-sharing.tsx" />
      {confirmingAll ? <ConfirmWarning confirmLabel="Share with everyone" disabled={busy} onCancel={() => {
      everyoneRef.current?.focus();
      setConfirmingAll(false);
    }} onConfirm={() => {
      setConfirmingAll(false);
      void save({
        mode: 'all'
      });
    }}>
          Share {view.name} with everyone who can use {controller.definition.label}?
        </ConfirmWarning> : null}
      {mode === 'selected' && summary.mode === 'selected' && unlisted > 0 ? <p className="text-xs text-muted-foreground">
          Shared with {unlisted} more {unlisted === 1 ? 'person' : 'people'} not listed here.
        </p> : null}
      {sharedUsers.error || sharedRoles.error ? <p className="text-xs text-destructive" role="alert">
          Couldn’t load who this is shared with.
        </p> : null}
      {error ? <p className="text-sm text-destructive" role="alert">
          {error}
        </p> : null}
    </div>;
};

/**
 * Audience entries carry no email, which the picker only uses to search its own options.
 * Inactive people stay listed so the owner can remove them.
 */
const toPickerUser = (entry: SavedViewAudienceEntry): FormUserMultiSelectUser[] => entry.type === 'user' ? [{
  email: '',
  id: entry.id,
  last_name: entry.last_name,
  name: entry.name
}] : [];

/** Sharing for Save as new: the same choices as {@link ViewSharing}, kept until the view saves. */
export const AudiencePicker = t0 => {
  const $ = _c(4);
  const {
    onChange,
    value
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Label data-sentry-element="Label" data-sentry-source-file="view-sharing.tsx">Sharing</Label>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onChange || $[2] !== value) {
    t2 = <div className="flex flex-col gap-3" data-sentry-component="AudiencePicker" data-sentry-source-file="view-sharing.tsx">{t1}<AudienceFields onChange={onChange} value={value} data-sentry-element="AudienceFields" data-sentry-source-file="view-sharing.tsx" /></div>;
    $[1] = onChange;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};

/** The mode, then People and Roles on their own rows while Selected. */
const AudienceFields = t0 => {
  const $ = _c(29);
  const {
    disabled: t1,
    everyoneRef,
    onChange,
    value
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      permissions: [permissions.users.list]
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    allowed: canAddUsers
  } = useAccess(t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      permissions: [permissions.roles.list]
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    allowed: canAddRoles
  } = useAccess(t3);
  const {
    label
  } = useTableConfiguration().definition;
  const selected = value.mode === "selected";
  let t4;
  if ($[2] !== canAddRoles || $[3] !== canAddUsers || $[4] !== disabled || $[5] !== everyoneRef || $[6] !== onChange || $[7] !== selected || $[8] !== value) {
    t4 = MODES.map(option => <Button aria-pressed={value.mode === option.mode} disabled={disabled || option.mode === "selected" && !canAddUsers && !canAddRoles && !selected || option.mode === "all" && !canAddUsers && value.mode !== "all"} key={option.mode} onClick={() => onChange({
      ...value,
      mode: option.mode
    })} ref={option.mode === "all" ? everyoneRef : undefined} size="sm" type="button" variant={value.mode === option.mode ? "default" : "outline"}>{option.label}</Button>);
    $[2] = canAddRoles;
    $[3] = canAddUsers;
    $[4] = disabled;
    $[5] = everyoneRef;
    $[6] = onChange;
    $[7] = selected;
    $[8] = value;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = <div aria-label="Who can see this view" className="flex gap-1" role="group">{t4}</div>;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== label || $[13] !== value.mode) {
    t6 = value.mode === "all" ? <p className="text-sm text-muted-foreground">Everyone who can use {label} can see this view.</p> : null;
    $[12] = label;
    $[13] = value.mode;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== value.mode) {
    t7 = value.mode === "owner" ? <p className="text-sm text-muted-foreground">Only you can see this view.</p> : null;
    $[15] = value.mode;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== canAddRoles || $[18] !== canAddUsers || $[19] !== disabled || $[20] !== onChange || $[21] !== selected || $[22] !== value) {
    t8 = selected ? <><PeopleField disabled={disabled} onChange={users => onChange({
        ...value,
        users
      })} readOnly={!canAddUsers} value={value.users} /><RolesField disabled={disabled} onChange={roles => onChange({
        ...value,
        roles
      })} readOnly={!canAddRoles} value={value.roles} /><p className="text-xs text-muted-foreground">Everyone in these Roles, now and later, can see the view. With nobody chosen, only you can.</p></> : null;
    $[17] = canAddRoles;
    $[18] = canAddUsers;
    $[19] = disabled;
    $[20] = onChange;
    $[21] = selected;
    $[22] = value;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t5 || $[25] !== t6 || $[26] !== t7 || $[27] !== t8) {
    t9 = <div className="flex flex-col gap-4" data-sentry-component="AudienceFields" data-sentry-source-file="view-sharing.tsx">{t5}{t6}{t7}{t8}</div>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
};

/** Active Users to choose from, searched by the API; chosen people stay among the options. */
const PeopleField = t0 => {
  const $ = _c(20);
  const {
    disabled,
    onChange,
    readOnly,
    value
  } = t0;
  const api = useApi();
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== api.users.list || $[1] !== search) {
    t1 = api.users.list.queryOptions({
      input: {
        "filter[statuses]": "active",
        perPage: 100,
        search: search.trim() || undefined
      }
    });
    $[0] = api.users.list;
    $[1] = search;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = open && !readOnly;
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      ...t1,
      enabled: t2
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const users = useQuery(t3);
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Label data-sentry-element="Label" data-sentry-source-file="view-sharing.tsx">People</Label>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== users.data?.data || $[8] !== value) {
    let t6;
    if ($[10] !== value) {
      t6 = user => !value.some(chosen => chosen.id === user.id);
      $[10] = value;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    t5 = [...value, ...(users.data?.data ?? []).filter(t6)];
    $[7] = users.data?.data;
    $[8] = value;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[12] !== disabled || $[13] !== onChange || $[14] !== readOnly || $[15] !== search || $[16] !== t5 || $[17] !== users.isFetching || $[18] !== value) {
    t6 = <div className="flex flex-col gap-1.5" data-sentry-component="PeopleField" data-sentry-source-file="view-sharing.tsx">{t4}<UserMultiSelect disabled={disabled} isLoading={users.isFetching} label="People" onChange={onChange} onOpenChange={setOpen} onSearchValueChange={setSearch} placeholder="Choose people" readOnly={readOnly} searchPlaceholder="Search people" searchValue={search} users={t5} value={value} data-sentry-element="UserMultiSelect" data-sentry-source-file="view-sharing.tsx" /></div>;
    $[12] = disabled;
    $[13] = onChange;
    $[14] = readOnly;
    $[15] = search;
    $[16] = t5;
    $[17] = users.isFetching;
    $[18] = value;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
};

/** Roles to share with; the System Role and other hidden Roles aren't offered. */
const RolesField = t0 => {
  const $ = _c(91);
  const {
    disabled,
    onChange,
    readOnly,
    value
  } = t0;
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  let t1;
  if ($[0] !== api.roles.roles) {
    t1 = api.roles.roles.queryOptions({
      input: {
        perPage: 100
      }
    });
    $[0] = api.roles.roles;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== open || $[3] !== t1) {
    t2 = {
      ...t1,
      enabled: open
    };
    $[2] = open;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const roles = useQuery(t2);
  let T0;
  let T1;
  let T2;
  let T3;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== disabled || $[6] !== onChange || $[7] !== open || $[8] !== readOnly || $[9] !== roles.data?.data || $[10] !== search || $[11] !== value) {
    const query = search.trim().toLowerCase();
    const choices = (roles.data?.data ?? []).filter(role => !HIDDEN_ROLES.includes(role.name) && role.name.toLowerCase().includes(query));
    let t24;
    if ($[37] !== value) {
      t24 = id => value.some(role_0 => role_0.id === id);
      $[37] = value;
      $[38] = t24;
    } else {
      t24 = $[38];
    }
    const isChosen = t24;
    let t25;
    if ($[39] !== isChosen || $[40] !== onChange || $[41] !== value) {
      t25 = role_1 => onChange(isChosen(role_1.id) ? value.filter(item => item.id !== role_1.id) : [...value, role_1]);
      $[39] = isChosen;
      $[40] = onChange;
      $[41] = value;
      $[42] = t25;
    } else {
      t25 = $[42];
    }
    const toggle = t25;
    t20 = "flex flex-col gap-1.5";
    t21 = "RolesField";
    t22 = "view-sharing.tsx";
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <Label data-sentry-element="Label" data-sentry-source-file="view-sharing.tsx">Roles</Label>;
      $[43] = t23;
    } else {
      t23 = $[43];
    }
    T3 = Tags;
    t12 = "w-full";
    t13 = setOpen;
    t14 = open;
    t15 = setSearch;
    t16 = search;
    t17 = "Tags";
    t18 = "view-sharing.tsx";
    const t26 = disabled || readOnly;
    let t27;
    if ($[44] !== disabled || $[45] !== readOnly || $[46] !== toggle || $[47] !== value) {
      t27 = value.length === 0 ? <span className="text-muted-foreground">Choose Roles</span> : value.map(role_2 => <TagsValue key={role_2.id} onRemove={disabled || readOnly ? undefined : () => toggle(role_2)} variant="outline">{formatRoleName(role_2.name)}</TagsValue>);
      $[44] = disabled;
      $[45] = readOnly;
      $[46] = toggle;
      $[47] = value;
      $[48] = t27;
    } else {
      t27 = $[48];
    }
    if ($[49] !== t26 || $[50] !== t27) {
      t19 = <TagsTrigger aria-label="Roles" disabled={t26} variant="outline" data-sentry-element="TagsTrigger" data-sentry-source-file="view-sharing.tsx">{t27}</TagsTrigger>;
      $[49] = t26;
      $[50] = t27;
      $[51] = t19;
    } else {
      t19 = $[51];
    }
    T2 = TagsContent;
    t9 = "TagsContent";
    t10 = "view-sharing.tsx";
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <TagsInput onValueChange={setSearch} placeholder={"Search Roles\u2026"} data-sentry-element="TagsInput" data-sentry-source-file="view-sharing.tsx" />;
      $[52] = t11;
    } else {
      t11 = $[52];
    }
    T1 = TagsList;
    t6 = "TagsList";
    t7 = "view-sharing.tsx";
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <TagsEmpty data-sentry-element="TagsEmpty" data-sentry-source-file="view-sharing.tsx">No Roles found.</TagsEmpty>;
      $[53] = t8;
    } else {
      t8 = $[53];
    }
    T0 = TagsGroup;
    t3 = "TagsGroup";
    t4 = "view-sharing.tsx";
    let t28;
    if ($[54] !== isChosen || $[55] !== toggle) {
      t28 = role_3 => <TagsItem key={role_3.id} onSelect={() => toggle(role_3)} value={role_3.name}>{formatRoleName(role_3.name)}{isChosen(role_3.id) ? <CheckIcon className="text-muted-foreground" size={14} /> : null}</TagsItem>;
      $[54] = isChosen;
      $[55] = toggle;
      $[56] = t28;
    } else {
      t28 = $[56];
    }
    t5 = choices.map(t28);
    $[5] = disabled;
    $[6] = onChange;
    $[7] = open;
    $[8] = readOnly;
    $[9] = roles.data?.data;
    $[10] = search;
    $[11] = value;
    $[12] = T0;
    $[13] = T1;
    $[14] = T2;
    $[15] = T3;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
    $[20] = t14;
    $[21] = t15;
    $[22] = t16;
    $[23] = t17;
    $[24] = t18;
    $[25] = t19;
    $[26] = t20;
    $[27] = t21;
    $[28] = t22;
    $[29] = t23;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
    $[33] = t6;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
  } else {
    T0 = $[12];
    T1 = $[13];
    T2 = $[14];
    T3 = $[15];
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
    t13 = $[19];
    t14 = $[20];
    t15 = $[21];
    t16 = $[22];
    t17 = $[23];
    t18 = $[24];
    t19 = $[25];
    t20 = $[26];
    t21 = $[27];
    t22 = $[28];
    t23 = $[29];
    t3 = $[30];
    t4 = $[31];
    t5 = $[32];
    t6 = $[33];
    t7 = $[34];
    t8 = $[35];
    t9 = $[36];
  }
  let t24;
  if ($[57] !== T0 || $[58] !== t3 || $[59] !== t4 || $[60] !== t5) {
    t24 = <T0 data-sentry-element={t3} data-sentry-source-file={t4}>{t5}</T0>;
    $[57] = T0;
    $[58] = t3;
    $[59] = t4;
    $[60] = t5;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== T1 || $[63] !== t24 || $[64] !== t6 || $[65] !== t7 || $[66] !== t8) {
    t25 = <T1 data-sentry-element={t6} data-sentry-source-file={t7}>{t8}{t24}</T1>;
    $[62] = T1;
    $[63] = t24;
    $[64] = t6;
    $[65] = t7;
    $[66] = t8;
    $[67] = t25;
  } else {
    t25 = $[67];
  }
  let t26;
  if ($[68] !== T2 || $[69] !== t10 || $[70] !== t11 || $[71] !== t25 || $[72] !== t9) {
    t26 = <T2 data-sentry-element={t9} data-sentry-source-file={t10}>{t11}{t25}</T2>;
    $[68] = T2;
    $[69] = t10;
    $[70] = t11;
    $[71] = t25;
    $[72] = t9;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  let t27;
  if ($[74] !== T3 || $[75] !== t12 || $[76] !== t13 || $[77] !== t14 || $[78] !== t15 || $[79] !== t16 || $[80] !== t17 || $[81] !== t18 || $[82] !== t19 || $[83] !== t26) {
    t27 = <T3 className={t12} onOpenChange={t13} open={t14} setValue={t15} value={t16} data-sentry-element={t17} data-sentry-source-file={t18}>{t19}{t26}</T3>;
    $[74] = T3;
    $[75] = t12;
    $[76] = t13;
    $[77] = t14;
    $[78] = t15;
    $[79] = t16;
    $[80] = t17;
    $[81] = t18;
    $[82] = t19;
    $[83] = t26;
    $[84] = t27;
  } else {
    t27 = $[84];
  }
  let t28;
  if ($[85] !== t20 || $[86] !== t21 || $[87] !== t22 || $[88] !== t23 || $[89] !== t27) {
    t28 = <div className={t20} data-sentry-component={t21} data-sentry-source-file={t22}>{t23}{t27}</div>;
    $[85] = t20;
    $[86] = t21;
    $[87] = t22;
    $[88] = t23;
    $[89] = t27;
    $[90] = t28;
  } else {
    t28 = $[90];
  }
  return t28;
};